/**
 * Native RevenueCat purchase bridge for Word Wheel Quest.
 * iOS shell should also expose `getAppUserID` / `getCustomerInfo` so plays can
 * store the RevenueCat App User ID (`$RCAnonymousID:…`).
 */

function getPurchaseBridge() {
    if (typeof window === "undefined") {
        return null;
    }
    if (window.PuzzleInteractPurchases?.purchasePackage) {
        return window.PuzzleInteractPurchases;
    }
    const capPlugin = window.Capacitor?.Plugins?.PuzzlePurchases;
    if (capPlugin?.purchasePackage) {
        return capPlugin;
    }
    return null;
}

export function isNativePurchasesAvailable() {
    return Boolean(getPurchaseBridge());
}

export async function getRevenueCatIdentity() {
    const empty = { revenueCatAppUserId: "", revenueCatOriginalAppUserId: "" };
    const bridge = getPurchaseBridge();
    if (!bridge) {
        return empty;
    }
    try {
        let revenueCatAppUserId = "";
        let revenueCatOriginalAppUserId = "";
        if (typeof bridge.getAppUserID === "function") {
            revenueCatAppUserId = String((await bridge.getAppUserID()) || "").trim();
        } else if (typeof bridge.getAppUserId === "function") {
            revenueCatAppUserId = String((await bridge.getAppUserId()) || "").trim();
        }
        const infoFn =
            typeof bridge.getCustomerInfo === "function"
                ? bridge.getCustomerInfo
                : typeof bridge.getPurchaserInfo === "function"
                  ? bridge.getPurchaserInfo
                  : null;
        if (infoFn) {
            const info = await infoFn.call(bridge);
            revenueCatOriginalAppUserId = String(
                info?.originalAppUserId || info?.originalAppUserID || ""
            ).trim();
            if (!revenueCatAppUserId) {
                revenueCatAppUserId = String(
                    info?.appUserId || info?.appUserID || info?.originalAppUserId || ""
                ).trim();
            }
        }
        return { revenueCatAppUserId, revenueCatOriginalAppUserId };
    } catch {
        return empty;
    }
}

/**
 * @param {{ packageKey: string, productId: string, offeringId?: string }} params
 * @returns {Promise<{ productId: string, transactionId: string, packageKey: string, rawPayload?: object }>}
 */
export async function purchaseRevenueCatPackage({
    packageKey,
    productId,
    offeringId = "default",
}) {
    const bridge = getPurchaseBridge();
    if (!bridge) {
        throw new Error("In-app purchases are available in the Word Wheel Quest app on iPhone.");
    }

    const result = await bridge.purchasePackage({
        packageKey,
        productId,
        offeringId,
    });

    const transactionId = result?.transactionId || result?.transaction?.transactionIdentifier;
    if (!transactionId) {
        throw new Error("Purchase completed but no transaction id was returned.");
    }

    return {
        productId: result?.productId || productId,
        transactionId: String(transactionId),
        packageKey: result?.packageKey || packageKey,
        rawPayload: result?.rawPayload || result,
    };
}
